import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { X, Home, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedText from '../components/AnimatedText';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ttStatsAPI } from '../services/api';
import { EloData, Competition, Game } from '../types/api.types';
import { parseDateForChart, parseDateToObject, parseDate } from '../utils/dateUtils';
import { cleanPlayerUrl } from '../utils/urlUtils';
import UnifiedFooter from '../components/UnifiedFooter';
import AppPromotion from '../components/AppPromotion';
import PlayerPageSkeleton from '../components/PlayerPageSkeleton';
import { useAuth } from '../contexts/AuthContext';
import { LoginModal } from '../components/LoginModal';
import { playerPageTranslations } from '../translations/playerPage';
import PlayerHeader from '../components/player/PlayerHeader';
import PlayerEloCard from '../components/player/PlayerEloCard';
import PlayerClassificationCard from '../components/player/PlayerClassificationCard';
import PlayerEloHistory from '../components/player/PlayerEloHistory';
import PlayerPerformanceStats from '../components/player/PlayerPerformanceStats';
import PlayerSetStatistics from '../components/player/PlayerSetStatistics';
import PlayerRecentMatches from '../components/player/PlayerRecentMatches';

const PlayerPage: React.FC = () => {
  const { url, lang } = useParams<{ url: string; lang?: string }>();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const [playerData, setPlayerData] = useState<EloData | null>(null);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [history, setHistory] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileAnimation, setProfileAnimation] = useState<any>(null);
  const [isLikelyFemale, setIsLikelyFemale] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showHearts, setShowHearts] = useState(false);
  const [showFavoritesLimitModal, setShowFavoritesLimitModal] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { user, isFavorite, addFavorite, removeFavorite, favorites } = useAuth();

  const t = playerPageTranslations[currentLang] || playerPageTranslations.en;

  useEffect(() => {
    if (url) {
      loadPlayerData(decodeURIComponent(url));
    }
  }, [url]);

  // Load Lottie animation based on player data
  useEffect(() => {
    if (!playerData) return;

    // Detect gender from games data
    // Check if player participates in women's leagues/competitions
    // Women's competitions contain keywords like "Woman", "Dame", "Dames", "Femme", "Femmes", "Donne"
    let detectedFemale = false;

    if (playerData.games && playerData.games.length > 0) {
      const femaleKeywords = ['woman', 'women', 'dame', 'dames', 'femme', 'femmes', 'donne'];
      const maleKeywords = ['herren', 'men', 'homme', 'hommes', 'uomini'];

      // Check if any game is in a women's competition
      detectedFemale = playerData.games.some((game: any) => {
        const competition = (game.counterparties || game.competition || '').toLowerCase();

        // First check if it's clearly a men's competition
        const isMaleCompetition = maleKeywords.some(keyword => {
          const regex = new RegExp(`\\b${keyword}\\b`, 'i');
          return regex.test(competition);
        });

        // If it's a male competition, don't count it as female even if "damen" appears
        if (isMaleCompetition) return false;

        // Check for female keywords using word boundaries
        return femaleKeywords.some(keyword => {
          const regex = new RegExp(`\\b${keyword}\\b`, 'i');
          return regex.test(competition);
        });
      });
    }

    // Update state
    setIsLikelyFemale(detectedFemale);

    // Select appropriate animation
    const animationPath = detectedFemale
      ? '/lottie/profile_woman.json'
      : '/lottie/profile_man.json';

    fetch(animationPath)
      .then(response => response.json())
      .then(data => setProfileAnimation(data))
      .catch(error => console.error('Error loading Lottie animation:', error));
  }, [playerData]);

  // Set timeout for loading state
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 seconds

      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  const loadPlayerData = async (playerUrl: string) => {
    setLoading(true);
    setError(null);

    try {
      // Clean the URL first
      const cleanUrl = cleanPlayerUrl(playerUrl);

      console.log('🔍 Loading player data:', { url: cleanUrl });

      // Use the unified /player endpoint which returns EVERYTHING in one request
      const playerData = await ttStatsAPI.getPlayerElo(cleanUrl);

      // The /player endpoint returns complete data including:
      // - Profile (name, club, classification, teams)
      // - ELO data (official_elo, games, elo_records, swissRank)
      // - Stats (performance stats, set stats)
      // - Achievements
      // - URLs (playerHref - stable, eloHref - current month)

      console.log('✅ Player data loaded:', {
        name: playerData.player_name,
        club: playerData.club,
        elo: playerData.current_elo,
        stable_url: playerData.stable_url
      });

      // Use games from player data
      const gamesData = playerData.games || [];

      setPlayerData(playerData);
      setCompetitions([]); // No competitions endpoint available

      // Sort games by date (newest first)
      const sortedHistory = gamesData.sort((a: any, b: any) => {
        const dateA = parseDateToObject(a.date);
        const dateB = parseDateToObject(b.date);
        return dateB.getTime() - dateA.getTime();
      });

      setHistory(sortedHistory);
    } catch (err: any) {
      console.error('❌ Error loading player data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: playerUrl
      });
      setError('Failed to load player data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (!playerData || !url) return;

    let playerName = playerData.player_name && playerData.player_name !== 'temp' && playerData.player_name !== 'Unknown' && playerData.player_name !== 'Unknown Player'
      ? playerData.player_name
      : url ? decodeURIComponent(url).split('/').pop()?.replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Player' : 'Player';

    // Convert "LastName, FirstName" to "FirstName LastName" for favorites
    if (playerName.includes(',')) {
      const [lastName, firstName] = playerName.split(',').map(s => s.trim());
      playerName = `${firstName} ${lastName}`;
    }

    const playerLink = url;

    try {
      if (isFavorite(playerLink)) {
        await removeFavorite(playerLink);
      } else {
        // Check if user has reached the 3 favorites limit
        if (favorites.length >= 3) {
          setShowFavoritesLimitModal(true);
          return;
        }

        // ALWAYS use stable playerPortrait URL (playerHref) from API
        // This URL never expires and is the recommended way to store player links
        const linkToStore = playerData.stable_url
          ? cleanPlayerUrl(playerData.stable_url)
          : cleanPlayerUrl(playerData.playerHref || playerLink);

        console.log('💾 Storing favorite with stable URL:', {
          original: playerLink,
          stable: linkToStore,
          playerHref: playerData.playerHref
        });

        // Pass player data to store ELO, club, classification, license number
        await addFavorite(playerName, linkToStore, {
          club: playerData.club || '',
          elo: playerData.current_elo || 0,
          classification: playerData.classification || '',
          license_number: playerData.licenseNumber || ''
        });

        // Trigger hearts animation when adding to favorites
        setShowHearts(true);
        setTimeout(() => setShowHearts(false), 3000); // Hide after 3 seconds
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  if (loading && !loadingTimeout) {
    return <PlayerPageSkeleton />;
  }

  if (error || !playerData || loadingTimeout) {
    const errorTranslations = {
      en: {
        title: 'Player Not Found',
        subtitle: loadingTimeout
          ? 'This is taking longer than expected'
          : 'We couldn\'t find this player',
        message: loadingTimeout
          ? 'The player might not exist or their data is unavailable.'
          : 'This player may not exist in our database.',
        helpTitle: 'Did this player play in the last 12 months? Are they licensed?',
        helpMessage: 'If yes, please report this to',
        backToSearch: 'Back to Search'
      },
      fr: {
        title: 'Joueur Introuvable',
        subtitle: loadingTimeout
          ? 'Cela prend plus de temps que prévu'
          : 'Nous n\'avons pas trouvé ce joueur',
        message: loadingTimeout
          ? 'Le joueur n\'existe peut-être pas ou ses données ne sont pas disponibles.'
          : 'Ce joueur n\'existe peut-être pas dans notre base de données.',
        helpTitle: 'Ce joueur a-t-il joué au cours des 12 derniers mois ? Est-il licencié ?',
        helpMessage: 'Si oui, veuillez le signaler à',
        backToSearch: 'Retour à la Recherche'
      },
      de: {
        title: 'Spieler nicht gefunden',
        subtitle: loadingTimeout
          ? 'Das dauert länger als erwartet'
          : 'Wir konnten diesen Spieler nicht finden',
        message: loadingTimeout
          ? 'Der Spieler existiert möglicherweise nicht oder seine Daten sind nicht verfügbar.'
          : 'Dieser Spieler existiert möglicherweise nicht in unserer Datenbank.',
        helpTitle: 'Hat dieser Spieler in den letzten 12 Monaten gespielt? Ist er lizenziert?',
        helpMessage: 'Wenn ja, bitte melde dies an',
        backToSearch: 'Zurück zur Suche'
      },
      it: {
        title: 'Giocatore Non Trovato',
        subtitle: loadingTimeout
          ? 'Ci sta impiegando più tempo del previsto'
          : 'Non siamo riusciti a trovare questo giocatore',
        message: loadingTimeout
          ? 'Il giocatore potrebbe non esistere o i suoi dati non sono disponibili.'
          : 'Questo giocatore potrebbe non esistere nel nostro database.',
        helpTitle: 'Questo giocatore ha giocato negli ultimi 12 mesi? È tesserato?',
        helpMessage: 'Se sì, segnalalo a',
        backToSearch: 'Torna alla Ricerca'
      }
    };

    const et = errorTranslations[currentLang] || errorTranslations.en;

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4 transition-colors duration-300">
        <div className="text-center max-w-2xl mx-auto">
          {/* Lottie Animation with glow effect */}
          <motion.div
            className="relative w-80 h-80 mx-auto mb-2"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Subtle glow behind animation */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(28, 122, 214, 0.08) 0%, transparent 70%)',
              }}
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.3, 0.5],
              }}
              transition={{
                duration: 3,
                ease: "easeInOut",
                repeat: Infinity
              }}
            />

            <DotLottieReact
              src="/404.lottie"
              loop
              autoplay
              className="relative z-10"
            />
          </motion.div>

          {/* Title with AnimatedText */}
          <div className="mb-4">
            <AnimatedText
              text={et.title}
              className="text-4xl md:text-5xl font-light text-gray-900 dark:text-gray-100 mb-2 transition-colors duration-300"
              delay={0.3}
              staggerDelay={0.02}
            />
          </div>

          {/* Subtitle */}
          <motion.p
            className="text-lg text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {et.subtitle}
          </motion.p>

          {/* Message */}
          <motion.p
            className="text-sm text-gray-500 dark:text-gray-400 mb-8 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {et.message}
          </motion.p>

          {/* Help Card */}
          <motion.div
            className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-2xl p-6 mb-8 max-w-lg mx-auto transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2 transition-colors duration-300">
              {et.helpTitle}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
              {et.helpMessage}{' '}
              <a
                href="mailto:doruk@ttstats.ch"
                className="text-[#1C7AD6] hover:text-[#1565C0] font-medium hover:underline transition-colors"
              >
                doruk@ttstats.ch
              </a>
            </p>
          </motion.div>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <Link
              to={`/${currentLang}`}
              className="group inline-flex items-center gap-2 px-8 py-3 bg-[#1C7AD6] hover:bg-[#1565C0] text-white font-medium rounded-xl transition-all shadow-sm hover:shadow-md"
            >
              <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
              {et.backToSearch}
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // Parse dates properly for time-based chart (sorted by date for proper timeline)
  const chartData = playerData.elo_history
    .filter(entry => entry.date) // Filter out entries with null/undefined dates
    .map(entry => {
      const parts = entry.date.split('.');
      let dateObj: Date;
      if (parts.length === 3) {
        // DD.MM.YYYY format
        const [day, month, year] = parts;
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        // Fallback to ISO parsing
        dateObj = new Date(entry.date);
      }
      return {
        date: dateObj.getTime(), // Use timestamp for proper time scale
        dateLabel: parseDateForChart(entry.date),
        elo: entry.elo,
        fullDate: parseDate(entry.date)
      };
    })
    .sort((a, b) => a.date - b.date); // Sort by date (oldest first for proper chart timeline)

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col transition-colors duration-300">
      {/* Floating App Promotion - show when scrolled */}
      {playerData && <AppPromotion compact lang={currentLang} />}
      
      {/* Google-style centered layout */}
      <div className="max-w-6xl mx-auto px-4 py-8 flex-1 w-full">
        {/* Player Header Component */}
        <PlayerHeader
          profileAnimation={profileAnimation}
          playerData={playerData}
          url={url}
          isLikelyFemale={isLikelyFemale}
          currentLang={currentLang}
          isFavorite={isFavorite}
          handleFavoriteToggle={handleFavoriteToggle}
          t={{ license: t.license }}
        />

        {/* Wrapped 2025 Promotional Card */}
        <motion.div
          className="max-w-6xl mx-auto mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link
            to={`/${currentLang}/wrapped/2025/${encodeURIComponent(url || '')}`}
            className="block group"
          >
            <div className="relative overflow-hidden rounded-2xl bg-[#1C7AD6] p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
              {/* Animated background effect */}
              <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                  >
                    <Sparkles className="w-12 h-12 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">
                      {t.wrappedTitle}
                    </h3>
                    <p className="text-white/90 text-sm md:text-base">
                      {t.wrappedSubtitle}
                    </p>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-xl text-white font-medium group-hover:bg-white/30 transition-colors">
                    {t.wrappedButton}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Cards Container - Side by Side on Desktop */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ELO Rating Card Component */}
            <PlayerEloCard
              playerData={playerData}
              isLikelyFemale={isLikelyFemale}
              currentLang={currentLang}
              t={{ currentEloRating: t.currentEloRating, projected: t.projected }}
            />

            {/* Classification Progress Card Component */}
            <PlayerClassificationCard
              playerData={playerData}
              isLikelyFemale={isLikelyFemale}
              currentLang={currentLang}
              t={{ classificationProgress: t.classificationProgress, pointsTo: t.pointsTo }}
            />
          </div>
        </div>

        {/* ELO History Chart Component */}
        <PlayerEloHistory
          chartData={chartData}
          isLikelyFemale={isLikelyFemale}
          currentLang={currentLang}
          t={{ eloHistory: t.eloHistory, games: t.games, months: t.months, viewAll: t.viewAllMatches }}
        />

        {/* Performance Stats Component */}
        <PlayerPerformanceStats
          playerData={playerData}
          currentLang={currentLang}
          t={{
            performanceStats: t.performanceStats,
            basedOnLast12Months: t.basedOnLast12Months,
            totalGames: t.totalGames,
            eloChange: t.eloChange,
            overallWinRate: t.overallWinRate,
            vsHigherElo: t.vsHigherElo,
            vsLowerElo: t.vsLowerElo
          }}
        />

        {/* Set Statistics Component */}
        <PlayerSetStatistics
          playerData={playerData}
          t={{
            setStatistics: t.setStatistics,
            setStatsDescription: t.setStatsDescription,
            matchesWithScores: t.matchesWithScores,
            matches: t.matches,
            losses: t.losses,
            wins: t.wins,
            winRateWhenWonFirst: t.winRateWhenWonFirst,
            winRateWhenLostFirst: t.winRateWhenLostFirst,
            reverseSweeps: t.reverseSweeps,
            wonAfterLosingFirst: t.wonAfterLosingFirst,
            unlockMore: t.unlockMoreStats
          }}
        />

        {/* Recent Matches Component */}
        <PlayerRecentMatches
          history={history}
          currentLang={currentLang}
          t={{
            recentMatches: t.recentMatches,
            lastMatches: t.lastMatches,
            viewAllMatches: t.viewAllMatches,
            won: t.won,
            lost: t.lost
          }}
        />

        {/* Competitions Section - Keeping as is for now */}
        {/* Competitions - simple cards */}
        {competitions.length > 0 && (
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            
            transition={{ duration: 0.6 }}>
            <h2 className="text-lg font-extralight text-gray-600 mb-6">{t.competitions}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {competitions.map((comp, index) => (
                <motion.div
                  key={index}
                  className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  
                  transition={{ duration: 0.3 }}
                >
                  <div className="font-medium text-gray-900 mb-1">
                    {comp.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {comp.stats?.wins || 0}W - {comp.stats?.losses || 0}L
                  </div>
                  {comp.stats && comp.stats.played > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                      {((comp.stats.wins / comp.stats.played) * 100).toFixed(0)}% {t.winRateShort}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer with App Showcase */}
      <UnifiedFooter variant="showcase" lang={currentLang} />

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        currentLang={currentLang}
      />

      {/* Hearts Animation - Rising from bottom when favorited */}
      <AnimatePresence>
        {showHearts && (
          <motion.div
            className="fixed inset-x-0 bottom-0 pointer-events-none z-50 flex justify-center"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: -200 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeOut" }}
          >
            <div className="w-96 h-96">
              <DotLottieReact
                src="/hearts_going_up.lottie"
                loop={false}
                autoplay
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Favorites Limit Modal */}
      <AnimatePresence>
        {showFavoritesLimitModal && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[100]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFavoritesLimitModal(false)}
            />

            {/* Dialog */}
            <motion.div
              className="fixed inset-0 z-[101] flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFavoritesLimitModal(false)}
            >
              <motion.div
                className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md"
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="relative bg-blue-50 px-6 py-4 border-b border-blue-100">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t.favoritesLimitTitle}
                  </h3>
                  <button
                    onClick={() => setShowFavoritesLimitModal(false)}
                    className="absolute top-4 right-4 p-1 rounded-lg hover:bg-blue-100/50 transition-colors"
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-5">
                  <p className="text-sm text-gray-600 mb-6">
                    {t.favoritesLimitMessage}
                  </p>

                  {/* App Store Badges */}
                  <div className="flex flex-col gap-3 mb-6">
                    <a
                      href="https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-opacity hover:opacity-80"
                    >
                      <img
                        src="/app-store-badge.svg"
                        alt="Download on App Store"
                        className="h-12 w-auto mx-auto"
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-opacity hover:opacity-80"
                    >
                      <img
                        src="/google-play-badge.svg"
                        alt="Get it on Google Play"
                        className="h-12 w-auto mx-auto"
                      />
                    </a>
                  </div>

                  {/* Cancel Button */}
                  <div className="flex justify-end">
                    <motion.button
                      onClick={() => setShowFavoritesLimitModal(false)}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {t.cancel}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlayerPage;